// Products → Linking sheet (decision 157, owner/office): every vendor code, QuickBooks product and sales name not linked
// to a product, in one Excel file with a guess where the words match. The owner fills the Product column (a list of the
// products, or "ignore"), uploads it, sees what it would do, and applies it. Applying goes through the same checks and
// audit as the Vendor codes, QuickBooks matching and Unknown names screens. JL: "can we just do an excel sheet".
import type { LinkingApplied, LinkingPreview, LinkingRow } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { ErrorBox } from '../components/ui';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';

const KIND: Record<LinkingRow['kind'], AppMessageKey> = { vendor_code: 'linkKindCode', qbo_item: 'linkKindQbo', name: 'linkKindName', box_content: 'linkKindBox' };
const ACTION: Record<LinkingRow['action'], AppMessageKey> = { link: 'linkActLink', guess: 'linkActGuess', set: 'linkActSet', ignore: 'linkActIgnore', done: 'linkActDone', problem: 'linkActProblem' };
const BADGE: Record<LinkingRow['action'], string> = { link: 'badge badge-ok', guess: 'badge badge-warn', set: 'badge badge-ok', ignore: 'badge', done: 'badge', problem: 'badge badge-danger' };
/** the server applies this many rows a call */
const CHUNK = 50;

type Result = LinkingApplied['results'][number] & { label: string };

export function LinkingSheetPage() {
  const { t } = useLocale();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [downloading, setDownloading] = useState(false);
  const [reading, setReading] = useState(false);
  const [preview, setPreview] = useState<LinkingPreview | null>(null);
  const [fileName, setFileName] = useState('');
  const [applying, setApplying] = useState<{ n: number; total: number } | null>(null);
  const [results, setResults] = useState<Result[] | null>(null);
  // the app's guesses left as they were are linked only when the person says they checked them (AI proposes, people decide)
  const [guessesChecked, setGuessesChecked] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // a 400 says what is wrong with the file in words the person can act on
  const said = error instanceof ApiError && error.status === 400 && !error.unreadable ? (error.problem.detail ?? null) : null;

  const download = async () => {
    setError(null);
    setDownloading(true);
    try {
      const { blob, filename } = await api.linkingSheet();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      setError(e);
    } finally {
      setDownloading(false);
    }
  };

  const read = async (file: File) => {
    setError(null);
    setResults(null);
    setPreview(null);
    setGuessesChecked(false);
    setFileName(file.name);
    setReading(true);
    try {
      setPreview(await api.linkingPreview(file));
    } catch (e) {
      setError(e);
    } finally {
      setReading(false);
      if (input.current) input.current.value = '';
    }
  };

  // box contents first: a QuickBooks product sold by the pound needs its product's box content before it links (decision 166)
  const toApply = preview
    ? preview.rows.filter((r) => r.action === 'link' || r.action === 'set' || r.action === 'ignore' || (guessesChecked && r.action === 'guess')).sort((a, b) => Number(b.kind === 'box_content') - Number(a.kind === 'box_content'))
    : [];
  const apply = async () => {
    if (!preview || toApply.length === 0) return;
    setError(null);
    const out: Result[] = [];
    const labelOf = new Map(toApply.map((r) => [`${r.kind}:${r.ref}`, r.label]));
    setApplying({ n: 0, total: toApply.length });
    let i = 0;
    try {
      for (; i < toApply.length; i += CHUNK) {
        const chunk = toApply.slice(i, i + CHUNK);
        const res = await api.linkingApply(
          chunk.map((r) =>
            r.action === 'ignore'
              ? { kind: r.kind, ref: r.ref, ignore: true as const }
              : r.action === 'set'
                ? { kind: r.kind, ref: r.ref, content: r.content ?? undefined, contentUnit: r.contentUnit ?? undefined }
                : { kind: r.kind, ref: r.ref, itemId: r.itemId ?? undefined, ...(r.unit ? { unit: r.unit } : {}) },
          ),
        );
        out.push(...res.results.map((x) => ({ ...x, label: labelOf.get(`${x.kind}:${x.ref}`) ?? x.ref })));
        setApplying({ n: Math.min(i + CHUNK, toApply.length), total: toApply.length });
      }
    } catch (e) {
      setError(e);
      // what was not tried is said, not lost: upload the same sheet again and it picks up from there
      for (const r of toApply.slice(i)) out.push({ ref: r.ref, kind: r.kind, ok: false, detail: t('linkingNotTried'), label: r.label });
    } finally {
      setApplying(null);
      setResults(out);
      setPreview(null);
      // every list that shows links reads again
      void qc.invalidateQueries({ queryKey: ['vendor-codes'] });
      void qc.invalidateQueries({ queryKey: ['catalog'] });
      void qc.invalidateQueries({ queryKey: ['items'] });
      void qc.invalidateQueries({ queryKey: ['qbo'] });
      void qc.invalidateQueries({ queryKey: ['reports'] });
    }
  };

  const busy = downloading || reading || applying !== null;
  const ok = results?.filter((r) => r.ok) ?? [];
  const failed = results?.filter((r) => !r.ok) ?? [];
  // problems first, then what will be linked, so what needs a look is at the top
  const order: LinkingRow['action'][] = ['problem', 'guess', 'link', 'set', 'ignore', 'done'];
  const shown = preview ? [...preview.rows].sort((a, b) => order.indexOf(a.action) - order.indexOf(b.action) || a.rowNo - b.rowNo) : [];

  return (
    <div className="desk-page stack" data-testid="linking-sheet">
      <header className="desk-head">
        <div>
          <h1>{t('linkingTitle')}</h1>
          <p className="muted">{t('linkingHint')}</p>
        </div>
      </header>

      <ol className="linking-steps">
        <li>
          <strong>{t('linkingStep1')}</strong>
          <p className="muted">{t('linkingStep1Hint')}</p>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void download()}>
            {downloading ? t('linkingMaking') : t('linkingDownload')}
          </button>
        </li>
        <li>
          <strong>{t('linkingStep2')}</strong>
          <p className="muted">{t('linkingStep2Hint')}</p>
        </li>
        <li>
          <strong>{t('linkingStep3')}</strong>
          <p className="muted">{t('linkingStep3Hint')}</p>
          <input
            ref={input}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            hidden
            data-testid="linking-file"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void read(f);
            }}
          />
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => input.current?.click()}>
            {reading ? t('linkingReading') : t('linkingUpload')}
          </button>
        </li>
      </ol>

      {said ? (
        <div className="alert alert-error" role="alert">
          <p>{said}</p>
        </div>
      ) : error ? (
        <ErrorBox error={error} />
      ) : null}

      {preview ? (
        <section className="stack" aria-label={t('linkingPreviewTitle')}>
          <h2>
            {t('linkingPreviewTitle')} <span className="muted">· {fileName}</span>
          </h2>
          <div className="kpi-row">
            {(['link', 'guess', 'set', 'ignore', 'done', 'problem'] as const).filter((a) => a === 'link' || a === 'problem' || preview.counts[a] > 0).map((a) => (
              <div key={a} className="kpi" data-testid={`linking-count-${a}`}>
                <span className="kpi-label">{t(ACTION[a])}</span>
                <span className="kpi-value">{preview.counts[a]}</span>
              </div>
            ))}
          </div>
          {preview.rows.length === 0 ? (
            <p className="muted">{t('linkingNothingFilled')}</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table linking-table" aria-label={t('linkingPreviewTitle')}>
                <thead>
                  <tr>
                    <th className="num">{t('linkingRowCol')}</th>
                    <th>{t('linkingNameCol')}</th>
                    <th>{t('linkingProductCol')}</th>
                    <th>{t('linkingDoesCol')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <tr key={`${r.kind}:${r.ref}:${r.rowNo}`}>
                      <td className="num">{r.rowNo}</td>
                      <td>
                        {r.label}
                        <span className="cell-sub">{t(KIND[r.kind])}</span>
                      </td>
                      <td>{r.kind === 'box_content' ? (r.content !== null && r.contentUnit ? `${r.content} ${r.contentUnit}` : r.typed) : (r.productName ?? r.typed)}{r.unit ? ` · ${t('soldByUnit').replace('{unit}', r.unit)}` : ''}</td>
                      <td>
                        <span className={BADGE[r.action]}>{t(ACTION[r.action])}</span>
                        {r.problem ? <span className="cell-sub">{r.problem}</span> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview.counts.guess > 0 ? (
            <label className="check" data-testid="linking-guesses">
              <input type="checkbox" checked={guessesChecked} onChange={(e) => setGuessesChecked(e.target.checked)} />
              <span>{t('linkingGuessesChecked').replace('{n}', String(preview.counts.guess))}</span>
            </label>
          ) : null}
          <div className="toolbar">
            <button type="button" className="btn btn-primary" disabled={busy || toApply.length === 0} onClick={() => void apply()} data-testid="linking-apply">
              {applying ? t('linkingApplying').replace('{n}', String(applying.n)).replace('{total}', String(applying.total)) : t('linkingApply').replace('{n}', String(toApply.length))}
            </button>
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setPreview(null)}>
              {t('cancel')}
            </button>
          </div>
          {preview.counts.problem > 0 ? <p className="note">{t('linkingProblemsNote')}</p> : null}
        </section>
      ) : null}

      {applying && !preview ? <p className="muted">{t('linkingApplying').replace('{n}', String(applying.n)).replace('{total}', String(applying.total))}</p> : null}

      {results ? (
        <section className="stack" aria-label={t('linkingDoneTitle')} data-testid="linking-results">
          <h2>{t('linkingDoneTitle')}</h2>
          <p>{t('linkingDoneSummary').replace('{ok}', String(ok.length)).replace('{failed}', String(failed.length))}</p>
          {failed.length > 0 ? (
            <div className="table-wrap">
              <table className="data-table linking-table" aria-label={t('linkingFailedTitle')}>
                <thead>
                  <tr>
                    <th>{t('linkingNameCol')}</th>
                    <th>{t('linkingWhyCol')}</th>
                  </tr>
                </thead>
                <tbody>
                  {failed.map((r) => (
                    <tr key={`${r.kind}:${r.ref}`}>
                      <td>
                        {r.label}
                        <span className="cell-sub">{t(KIND[r.kind])}</span>
                      </td>
                      <td>{r.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          <p className="note">
            {t('linkingAfter')} <Link to="/reports/profit">{t('profitTitle')}</Link>
          </p>
        </section>
      ) : null}

      <Ignored />

      <p className="note">
        {t('linkingOneAtATime')} <Link to="/items/codes">{t('navVendorCodes')}</Link> · <Link to="/qbo/matching">{t('qboMatchTitle')}</Link> · <Link to="/items/inbox">{t('gapLinkInbox')}</Link>
      </p>
    </div>
  );
}

/** What was marked "not a product" (QuickBooks products and sales names), each with Ask again (decision 158). */
function Ignored() {
  const { t } = useLocale();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: qk.linkingIgnored, queryFn: ({ signal }) => api.linkingIgnored(signal) });
  const back = useMutation({
    mutationFn: (r: { kind: 'qbo_item' | 'name'; ref: string }) => api.linkingUnignore(r.kind, r.ref),
    onSuccess: (v) => qc.setQueryData(qk.linkingIgnored, v),
  });
  const rows = list.data?.rows ?? [];
  if (rows.length === 0) return null;
  return (
    <details className="card linking-ignored" data-testid="linking-ignored">
      <summary>{t('linkingIgnoredTitle').replace('{n}', String(rows.length))}</summary>
      <p className="muted">{t('linkingIgnoredHint')}</p>
      {back.error ? <ErrorBox error={back.error} /> : null}
      <ul className="list">
        {rows.map((r) => (
          <li key={`${r.kind}:${r.ref}`} className="row">
            <span className="row-main">
              <span className="row-title">{r.label}</span>
              <span className="row-sub">{r.kind === 'qbo_item' ? t('linkKindQbo') : `${t('linkKindName')} · ${r.where ?? ''}`}</span>
            </span>
            <button type="button" className="btn btn-secondary btn-sm" disabled={back.isPending} onClick={() => back.mutate({ kind: r.kind, ref: r.ref })}>
              {t('linkingAskAgain')}
            </button>
          </li>
        ))}
      </ul>
    </details>
  );
}
